import { getApiBaseUrl, getApiHeaders } from '@/store/api/headers';
import { API_ENDPOINTS } from '@/constants';
import type {
  GetSuggestionsForCallOutputDTO,
  GetSuggestionByIdOutputDTO,
  ArticleTagOutputDTO,
} from '@/types/dynamicSuggestion';

/**
 * DynamicAiSuggestionService: Fetches AI suggestions from backend
 * Follows same pattern as callService with native fetch
 */
class DynamicAiSuggestionService {
  /**
   * Fetch available tags for filtering suggestions
   * @returns Promise resolving to array of tags
   */
  async fetchAvailableTags(): Promise<ArticleTagOutputDTO[]> {
    const headers = await getApiHeaders();
    const response = await fetch(
      `${getApiBaseUrl()}${API_ENDPOINTS.DYNAMIC_SUGGESTION.BASE}/tags`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.tags ?? [];
  }

  /**
   * Fetch dynamic AI suggestions for a given contact with optional tags
   * @param contactId - The contact/call ID
   * @param tags - Optional array of tag IDs for filtering
   * @returns Promise resolving to suggestions response
   */
  async fetchDynamicAiSuggestions(
    contactId: string,
    tags?: number[]
  ): Promise<GetSuggestionsForCallOutputDTO> {
    const headers = await getApiHeaders();

    // Build query parameters for tags
    const queryParams = new URLSearchParams();
    if (tags && tags.length > 0) {
      tags.forEach(tag => queryParams.append('tags', String(tag)));
    }

    const url = new URL(
      `${getApiBaseUrl()}${API_ENDPOINTS.DYNAMIC_SUGGESTION.FOR_CALL}/${encodeURIComponent(contactId)}`
    );
    if (queryParams.toString()) {
      url.search = queryParams.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetch single suggestion by ID with full details
   * @param suggestionId - The suggestion ID
   * @returns Promise resolving to full suggestion details
   */
  async fetchSuggestionById(
    suggestionId: string
  ): Promise<GetSuggestionByIdOutputDTO> {
    const headers = await getApiHeaders();
    const response = await fetch(
      `${getApiBaseUrl()}${API_ENDPOINTS.DYNAMIC_SUGGESTION.BASE}/${encodeURIComponent(suggestionId)}`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }
}

export const dynamicAiSuggestionService = new DynamicAiSuggestionService();
